import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { faCheck, faCopy } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { handleCopyClick } from "@/pages/Private/pages/List/utils/handleCopyClick";

import { formatTotpSecretForDisplay } from "./totpSecret.util";

const COPIED_FLASH_MS = 1600;

interface ManualSetupKeyProps {
	/** The raw base32 TOTP secret behind the QR code. */
	secret: string;
}

/**
 * The manual-entry fallback under the 2FA QR code: a "Can't scan the QR code?" link that
 * reveals the TOTP secret in readable groups, with a copy button. It stays collapsed until
 * asked for, so scanning remains the default path and the secret is not on screen for nothing.
 */
export const ManualSetupKey: FunctionComponent<ManualSetupKeyProps> = ({ secret }) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.${key}`);

	const [isRevealed, setIsRevealed] = useState(false);
	const [isCopied, setIsCopied] = useState(false);

	useEffect(() => {
		if (!isCopied) {
			return;
		}

		const timer = setTimeout(() => setIsCopied(false), COPIED_FLASH_MS);

		return () => clearTimeout(timer);
	}, [isCopied]);

	const handleCopy = () => {
		// The raw key goes to the clipboard; the spaces are only for reading.
		handleCopyClick(secret);
		setIsCopied(true);
	};

	if (!isRevealed) {
		return (
			<button
				className="mx-auto mt-3 block text-sm font-medium text-brand-light hover:underline"
				type="button"
				onClick={() => setIsRevealed(true)}
			>
				{ts("cannotScanQrCode")}
			</button>
		);
	}

	return (
		<div
			className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3"
			data-testid="manual-setup-key"
		>
			<p className="text-[12.5px] text-text-meta">{ts("manualSetupHint")}</p>

			<div className="mt-2 text-[11.5px] font-semibold text-text-meta">{ts("manualSetupKey")}</div>

			<div className="mt-1 flex items-center gap-2">
				<code
					className="flex-1 select-all break-all rounded-md border border-gray-200 bg-white px-3 py-2 font-mono text-sm tracking-wider text-text"
					data-testid="manual-setup-secret"
				>
					{formatTotpSecretForDisplay(secret)}
				</code>

				<Button
					color={ButtonColor.ACTION_SECONDARY}
					image={<Icon className="mr-1.5 h-3.5 w-3.5" icon={isCopied ? faCheck : faCopy} />}
					size={ButtonSize.S}
					title={ts(isCopied ? "keyCopied" : "copyKey")}
					type="button"
					onClick={handleCopy}
				/>
			</div>
		</div>
	);
};
